import {findLast, isUndefined} from 'lodash';
import {connect} from 'react-redux';

import {AsyncMoreStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {
  isActivityInConversationRelevant,
  isConversationInTeamContext,
} from '../../../../helpers/models/conversationHelpers';
import {ActivityModel} from '../../../../models/api/activityModel';
import {AppState} from '../../../../store/appState';
import {getConversations} from '../../../../store/data/dataSelectors';
import {jumpToActivityAsyncAction} from '../../../../store/panels/viewer/viewerPanelActions';
import {ViewerPanelConversationPageState} from '../../../../store/panels/viewer/viewerPanelState';
import {UnreadBannerBelow} from './unreadBannerBelow';

/*
 * Props.
 */

interface UnreadBannerBelowContainerProps {
  panelId: ViewerPanelId;
  activities: ReadonlyArray<ActivityModel>;
  lastVisibleActivityDate: number;
  page: ViewerPanelConversationPageState;
}

/*
 * Selectors.
 */

function getActivities(_state: AppState, props: UnreadBannerBelowContainerProps) {
  return props.activities;
}

function getLastVisibleActivityDate(_state: AppState, props: UnreadBannerBelowContainerProps) {
  return props.lastVisibleActivityDate;
}

function getCurrentPage(_state: AppState, props: UnreadBannerBelowContainerProps) {
  return props.page;
}

function getIsAllActivitiesLoadedBelow(state: AppState, props: UnreadBannerBelowContainerProps) {
  const page = getCurrentPage(state, props);
  return page.moreStatusBottom === AsyncMoreStatusesEnum.ALL_LOADED;
}

function makeGetLastActivity() {
  return createSelector([getActivities], (activities) =>
    findLast(activities, isActivityInConversationRelevant),
  );
}

function makeGetHasUnreadActivityBelow(getLastActivity: ReturnType<typeof makeGetLastActivity>) {
  return createSelector(
    [getCurrentPage, getLastActivity, getLastVisibleActivityDate, getConversations],
    (currentPage, lastActivity, lastVisibleActivityDate, conversations) => {
      const {originalConversationState} = currentPage;
      if (!originalConversationState || !lastActivity || !lastVisibleActivityDate) {
        return false;
      }

      // If we're in a team context, we will always start at the bottom (unless we have an aroundActivityId)
      const conversation = conversations[currentPage.conversationId];
      const isInTeamContext =
        conversation && isConversationInTeamContext(currentPage.cellsContext, conversation);
      if (isInTeamContext && !currentPage.parameters.activity) {
        return false;
      }

      const hasMoreUnloadedActivitiesBelow = [
        AsyncMoreStatusesEnum.HAS_MORE,
        AsyncMoreStatusesEnum.LOADING,
      ].includes(currentPage.moreStatusBottom);

      // Is the last activity newer than the original seen at of the conversation
      const {seenAt} = originalConversationState;
      const isLastActivityNew = !seenAt || seenAt < lastActivity.date;
      const isLastActivityUnseen = lastVisibleActivityDate < lastActivity.date;
      return Boolean(hasMoreUnloadedActivitiesBelow || (isLastActivityNew && isLastActivityUnseen));
    },
  );
}

const mapStateToProps = makeMapStateToProps<UnreadBannerBelowContainerProps>()(() => {
  const getLastActivity = makeGetLastActivity();
  const getHasUnreadActivityBelow = makeGetHasUnreadActivityBelow(getLastActivity);
  return {
    hasUnreadActivityBelow: getHasUnreadActivityBelow,
    lastActivity: getLastActivity,
    isAllActivitiesLoadedBelow: getIsAllActivitiesLoadedBelow,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<UnreadBannerBelowContainerProps>()(({panelId}) => ({
  onUnreadBannerClick: (activityId: number | undefined) =>
    jumpToActivityAsyncAction(panelId, isUndefined(activityId) ? {date: Infinity} : {id: activityId}),
}));

/*
 * Component.
 */

export const UnreadBannerBelowContainer = connect(mapStateToProps, mapDispatchToProps)(UnreadBannerBelow);
